import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { Panel, SectionHeader } from '@/components/erp/primitives'
import { ErrorStrip, LoadingBlock } from '@/shared/ui/states'
import { useSession } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import { fmtNumber } from '@/shared/lib/format'
import { RankedBars, Sparkbars, OutcomeStrip, ChartFrame } from '@/shared/ui/charts'
import { Donut } from '@/shared/charts/Donut'
import { AskPanel, CouncilBoard } from '@/shared/ui/council'
import { listElections } from '@/features/elections/api'
import { getFloor } from '@/features/station-mates/api'
import { getDashboard as warRoomDashboard, getGraph } from '@/features/warroom/api'
import { analyticsSummary as incidentSummary } from '@/features/incidents/api'
import { transferAnalysis } from '@/features/relocation/api'
import { analyticsOverview } from '@/features/programs/api'
import { getAssay } from '@/features/dossier/api'
import { listPolls } from '@/features/polling/api'
import { domains, worst, type DomainCard, type Level, type Platform } from './model'
import { advise, ask, platformContext } from './council'
import { LivePulse } from './LivePulse'

/**
 * The landing page — the whole platform, in the order somebody should read it.
 *
 * ## What this page is for
 *
 * A campaign manager signs in with one question: what needs me. Every module answers a
 * narrower version of that well and none of them answers it across the platform, so the
 * default landing page used to be an administration screen — which answers a question
 * almost nobody arrives with.
 *
 * The reading order is deliberate and descends from what perishes to what waits:
 *
 * 1. **One sentence.** The worst thing on the platform, in plain words.
 * 2. **The live pulse.** The only moving thing, because social is the only quantity here
 *    that changes while somebody watches. Everything else is a record.
 * 3. **Six cards, one colour scale.** Each domain's state, its number, and a sentence.
 * 4. **What needs you today.** The findings merged and ranked by time pressure.
 * 5. **The shape of it.** A few cross-platform charts.
 * 6. **The council.** A real advisor run over everything above.
 *
 * ## The colour scale is the same everywhere on this page
 *
 * Four levels — critical, serious, watch, steady — plus `unknown` for a rollup that did not
 * return. They are used for nothing else: no category is ever painted in them, so a red tile
 * always means the same thing wherever it appears. And **`unknown` is not a fifth shade of
 * bad**: it is neutral and says why, because "nothing is wrong here" and "we could not look
 * here" rendering the same way is how a dashboard lies without a single wrong number.
 *
 * Every level is also written in text on the card, so none of this is colour-alone.
 */
const LEVEL_UI: Record<Level, { dot: string; ring: string; chip: string; word: string }> = {
  critical: { dot: 'bg-rose-500',    ring: 'border-l-rose-500',    chip: 'bg-rose-500/10 text-rose-700 dark:text-rose-300',       word: 'Needs you now' },
  serious:  { dot: 'bg-amber-500',   ring: 'border-l-amber-500',   chip: 'bg-amber-500/10 text-amber-700 dark:text-amber-300',    word: 'Needs you today' },
  watch:    { dot: 'bg-sky-500',     ring: 'border-l-sky-500',     chip: 'bg-sky-500/10 text-sky-700 dark:text-sky-300',          word: 'Worth watching' },
  steady:   { dot: 'bg-emerald-500', ring: 'border-l-emerald-500', chip: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300', word: 'Steady' },
  unknown:  { dot: 'bg-muted-foreground/50', ring: 'border-l-border', chip: 'bg-muted text-muted-foreground',                     word: 'Not readable' },
}

export function DashboardPage() {
  const user = useSession((s) => s.user)
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 60_000)
    return () => window.clearInterval(id)
  }, [])

  /*
     Seven sources, each allowed to fail on its own.

     A `Promise.all` that rejects as a unit would blank the entire landing page because one
     reader lacks one permission — and lacking a permission is the normal case, not the
     exception. Every call therefore carries its own `catch`, and a null becomes an
     explicitly "not readable" card rather than a zero.
  */
  const platform = useLoad<Platform>(async () => {
    const elections = await listElections().catch(() => [])
    const live = elections.find((e) => e.statusCode === 'POLLING')
      ?? elections.find((e) => e.statusCode === 'ENGAGING')
      ?? elections[0]

    const [floor, warRoom, graph, incidents, transfers, programmes, assay, polls] =
      await Promise.all([
        live ? getFloor(live.id).catch(() => null) : Promise.resolve(null),
        warRoomDashboard().catch(() => null),
        getGraph({ windowHours: 336, limit: 200 }).catch(() => null),
        incidentSummary({}).catch(() => null),
        transferAnalysis({ from: isoDaysAgo(90), to: today() }).catch(() => null),
        analyticsOverview({}).catch(() => null),
        getAssay({ windowDays: 90, limit: 200 }).catch(() => null),
        listPolls({}, 0, 50).then((p) => p.content).catch(() => null),
      ])
    return { floor, warRoom, graph, incidents, transfers, programmes, assay, polls }
  }, [])

  const p = platform.data
  const cards = useMemo(() => (p ? domains(p) : []), [p])
  const findings = useMemo(() => (p ? advise(p, cards) : []), [p, cards])
  const level = worst(cards)
  const top = findings[0]

  const greeting = now.getHours() < 12 ? 'Good morning'
    : now.getHours() < 17 ? 'Good afternoon' : 'Good evening'
  const name = (user?.name ?? '').split(' ')[0]

  return (
    <div className="space-y-4">
      <SectionHeader
        title={name ? `${greeting}, ${name}` : greeting}
        description={`Everything across the platform, as at ${now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}. Read top to bottom: what perishes first is highest.`}
        right={
          <button type="button" className="erp-btn" onClick={() => void platform.reload()}>
            Refresh
          </button>
        }
      />
      <ErrorStrip message={platform.error} />

      {platform.loading && !p ? <LoadingBlock label="Reading every module…" /> : !p ? null : (
        <>
          {/* ── one sentence ── */}
          <div className={cn('erp-card border-l-[4px] p-4', LEVEL_UI[level].ring)}>
            <div className="flex flex-wrap items-center gap-2">
              <span className={cn('inline-flex items-center gap-1.5 rounded px-2 py-0.5 text-erp-xs font-medium',
                LEVEL_UI[level].chip)}>
                <span className={cn('h-2 w-2 rounded-full', LEVEL_UI[level].dot)} aria-hidden />
                {LEVEL_UI[level].word}
              </span>
              <span className="text-erp-xs text-muted-foreground">
                {findings.length} {findings.length === 1 ? 'finding' : 'findings'} across {cards.length} areas
              </span>
            </div>
            <p className="mt-2 text-erp-lg font-medium">
              {top ? top.headline : 'Nothing across the platform crosses a threshold.'}
            </p>
            {top && <p className="mt-1 text-erp-sm text-muted-foreground">{top.action}</p>}
          </div>

          {/* ── the only moving thing ── */}
          <Panel
            title="Live pulse"
            description="Every social thread in the window, drifting at its own speed. This is the one thing on the page that is changing while you read it."
            right={p.graph ? (
              <Link to="/war-room" className="text-erp-xs text-muted-foreground hover:text-foreground underline">
                Open the war room
              </Link>
            ) : undefined}
          >
            <LivePulse graph={p.graph} />
          </Panel>

          {/* ── six cards ── */}
          <div className="grid gap-3"
               style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
            {cards.map((c) => <DomainTile key={c.id} card={c} />)}
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            <div className="lg:col-span-2 space-y-4">
              <CouncilBoard
                findings={findings}
                title="What needs you today"
                description="Every module's worst problem in one queue, ranked by what gets worse while nobody looks at it — not by which number is biggest. Each card's stake is in its own module's units, so they are never added together."
                stakeUnit="unit"
                hideTotal
                emptyMessage="Nothing anywhere crosses a threshold. This is the moment for the work that never becomes urgent."
              />
              <Shape platform={p} />
            </div>

            <AskPanel
              storageKey="platform-council-history"
              description="Plain language, across every module. The figures above go with the question."
              placeholder="What should I do first today?"
              scopeNote="It sees the summary figures on this page — not individual voters, threads or records."
              suggestions={[
                'What should I do first today?',
                'What is the biggest risk right now?',
                'What can wait until tomorrow?',
                'What would an opponent attack first?',
                'Where is our data letting us down?',
              ]}
              onAsk={(q) => ask(q, findings)}
              context={() => platformContext(p, cards, findings)}
            />
          </div>
        </>
      )}
    </div>
  )
}

/**
 * One domain, as a tile.
 *
 * The level word is written next to the dot rather than implied by it, and the whole tile is
 * a link — a reader who sees a red card should be one click from the screen that explains it,
 * not hunting the sidebar for the module it belongs to.
 */
function DomainTile({ card }: { card: DomainCard }) {
  const ui = LEVEL_UI[card.level]
  return (
    <Link to={card.to}
          className={cn('erp-card border-l-[4px] p-4 block transition-colors hover:bg-muted/40',
            ui.ring)}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-erp-xs uppercase tracking-wide text-muted-foreground">
          {card.label}
        </span>
        <span className={cn('inline-flex items-center gap-1.5 rounded px-1.5 py-0.5 text-erp-xs',
          ui.chip)}>
          <span className={cn('h-1.5 w-1.5 rounded-full', ui.dot)} aria-hidden />
          {ui.word}
        </span>
      </div>

      <p className="mt-1.5 text-erp-2xl font-semibold tabular-nums leading-none">{card.value}</p>
      <p className="text-erp-xs text-muted-foreground">{card.unit}</p>

      {card.series.length > 1 && (
        <div className="mt-2">
          <Sparkbars points={card.series.map((v, i) => ({ label: String(i + 1), value: v }))}
                     height={40} emptyMessage="" />
        </div>
      )}

      <p className="mt-2 text-erp-sm leading-snug">{card.headline}</p>

      {card.facts.length > 0 && (
        <dl className="mt-2 flex flex-wrap gap-x-4 gap-y-0.5">
          {card.facts.map((f) => (
            <div key={f.label} className="flex items-baseline gap-1.5">
              <dt className="text-erp-xs text-muted-foreground">{f.label}</dt>
              <dd className="text-erp-xs tabular-nums font-medium">{f.value}</dd>
            </div>
          ))}
        </dl>
      )}
    </Link>
  )
}

/** A few cross-platform readings, each from a different module, drawn the same way. */
function Shape({ platform }: { platform: Platform }) {
  const inc = platform.incidents
  const f = platform.floor
  const g = platform.graph
  const d = platform.transfers

  return (
    <div className="grid gap-4 md:grid-cols-2">
      {f?.totals && (
        <Panel title="Where every member is" description="The turnout operation, accounted for.">
          <OutcomeStrip parts={[
            { label: 'Voted', value: f.totals.voted, tone: 'good' },
            { label: 'In a queue', value: f.totals.inQueue, tone: 'warn' },
            { label: 'En route', value: f.totals.enRoute, tone: 'neutral' },
            { label: 'Not moved',
              value: Math.max(f.totals.members - f.totals.voted - f.totals.inQueue - f.totals.enRoute, 0),
              tone: 'bad' },
          ]} />
        </Panel>
      )}

      {inc && inc.total > 0 && (
        <Panel title="Incidents by severity"
               description="An ordered scale on the ordinal ramp, never re-sorted by size.">
          <Donut
            palette="ordinal"
            slices={['LOW', 'MEDIUM', 'HIGH', 'CRITICAL']
              .map((k) => ({ id: k, label: k.charAt(0) + k.slice(1).toLowerCase(), value: inc.bySeverity[k] ?? 0 }))}
            total={inc.total}
            centreLabel="Incidents"
            formatValue={(v) => `${Math.round(v)}`}
            emptyMessage="Nothing recorded."
          />
        </Panel>
      )}

      {g && g.platforms.length > 0 && (
        <Panel title="Where the audience is"
               description="Engagements by platform. A platform we cannot reply on is a different plan, not a smaller one.">
          <RankedBars
            points={g.platforms.filter((x) => x.threads > 0)
              .sort((a, b) => b.engagements - a.engagements)
              .map((x) => ({
                label: `${x.label}${x.canPost ? '' : ' — watch only'}`,
                value: x.engagements,
                hint: `${x.threads} threads`,
              }))}
            tone={(l) => (l.includes('watch only') ? 'warn' : undefined)}
            emptyMessage="No platform reported a thread."
          />
        </Panel>
      )}

      {d && (d.recordedDaily ?? []).length > 1 && (
        <Panel title="Registrations recorded" description="Per day, over the last ninety.">
          <ChartFrame title="Recorded per day">
            <Sparkbars
              points={(d.recordedDaily ?? []).map((x) => ({ label: x.date.slice(5), value: x.count }))}
              height={90}
              emptyMessage="Nothing recorded in this window."
            />
          </ChartFrame>
        </Panel>
      )}
    </div>
  )
}

function today() { return new Date().toISOString().slice(0, 10) }
function isoDaysAgo(days: number) {
  return new Date(Date.now() - days * 86_400_000).toISOString().slice(0, 10)
}

export { fmtNumber }
